import { FC, memo, useCallback } from 'react'
import { useHistory } from 'react-router-dom'

import { Avatar } from '@/components/atoms/Avatar'
import { FollowButton } from '@/components/molecules/FollowButton'
import { MarkdownUrlHighlighter } from '@/components/molecules/MarkdownUrlHighlighter'
import { SocialsList } from '@/components/molecules/SocialsList'

import { AccountInfo } from '@/services/account'
import { UserInfo } from '@/services/user'
import { IWallet } from '@/services/wallet/getWallet'
import { useChat } from '@/lib/hooks/chat/useChat'
import { useVisualViewport } from '@/lib/hooks/useVisualViewport'
import { EventName, Tracking, TrackingEvent } from '@/lib/tracking'
import { Routes } from '@/router/routes'

import { FollowStatus } from '@/enums'

import {
  CurrencyButton,
  FollowersFollowingButtons,
  SendTipButton,
  XPButton,
} from '../'
import { ChatButton } from '../ChatButton'

type HeaderProps = {
  userId: UUID
  isAuthUser: boolean
  userInfo: UserInfo
  accountInfoData: AccountInfo
  wallet: IWallet
}

const UserProfileHeaderComponent: FC<HeaderProps> = ({
  userId,
  isAuthUser,
  userInfo,
  accountInfoData,
  wallet,
}) => {
  const {
    data: chatData,
    isError: isChatDataError,
    isLoading: isChatDataLoading,
  } = useChat({
    userId,
  })
  const history = useHistory()
  const {
    visualViewport: { width: viewportWidth },
  } = useVisualViewport()

  const trackFollowStatus = (eventName: EventName) => {
    Tracking.triggerEvent(eventName, {
      profileUserId: userInfo?.id,
    })
  }

  const handleOnFollow = (followStatus: FollowStatus) => {
    // click event on 'follow' or on 'follow back' button of profile page of another user
    if (
      [
        followStatus === FollowStatus.Follow,
        followStatus === FollowStatus.FollowBack,
      ].includes(true)
    ) {
      trackFollowStatus(TrackingEvent.ProfileFollowed)
    }

    // click event on 'following' or on 'friends' button of profile page of another user
    if (
      [
        followStatus === FollowStatus.Following,
        followStatus === FollowStatus.Friends,
      ].includes(true)
    ) {
      trackFollowStatus(TrackingEvent.ProfileUnfollowed)
    }
  }

  const handleAvatarClick = useCallback(() => {
    if (isAuthUser) history.push(Routes.PROFILE_EDIT)
  }, [history, isAuthUser])

  if (!userInfo) return

  return (
    <div className="w-full max-w-[40rem] mx-auto bg-cool-gray-900">
      <div className="flex flex-col justify-between p-3 px-4">
        <div className="flex items-end pb-3">
          <div onClick={handleAvatarClick}>
            <Avatar
              userId={userInfo?.id}
              size={viewportWidth < 640 ? 'xl' : 'xxl'}
              isModerator={userInfo?.isModerator}
              isAmbassador={userInfo?.isAmbassador}
              badge={userInfo?.badge}
            />
          </div>
          <div className="flex flex-col items-start ml-4">
            <div className="ml-4">
              <FollowersFollowingButtons
                userId={userInfo?.id}
                followingCount={userInfo?.followingCount}
                followerCount={userInfo?.followerCount}
              />
            </div>
            <div className="flex justify-center bg-cool-gray-800 rounded-full py-2 pl-3 pr-2 mb-1 mt-3">
              <CurrencyButton
                value={wallet?.totalBalance ?? '0'}
                isViewingOwnProfile={isAuthUser}
              />
              <div className="ml-5 mr-4">
                <XPButton
                  badge={userInfo?.badge}
                  value={Math.floor(accountInfoData?.xp || 0)}
                  isViewingOwnProfile={isAuthUser}
                />
              </div>
            </div>
          </div>
        </div>
        <div
          className={`flex items-center ${
            userInfo?.displayName ? 'place-content-between' : 'justify-end'
          }`}
        >
          {!!userInfo?.displayName && (
            <span className="text-lg sm:text-xl font-bold py-2">
              {userInfo?.displayName}
            </span>
          )}
          <SocialsList isAuthUser={isAuthUser} userId={userId} />
        </div>

        {!!userInfo?.bio && (
          <MarkdownUrlHighlighter
            className="text-sm break-words"
            text={userInfo?.bio}
          />
        )}
        {!isAuthUser && (
          <div className="flex flex-row justify-start items-center pt-6">
            <FollowButton
              size="small"
              userId={userId}
              followStatus={userInfo?.followStatus}
              username={userInfo?.username}
              onFollow={handleOnFollow}
            />
            <SendTipButton className="ml-2" userInfo={userInfo} />
            <div className="ml-2">
              <ChatButton
                userInfo={userInfo}
                isLoading={isChatDataLoading}
                chatData={chatData}
                isError={isChatDataError}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export const UserProfileHeader = memo(UserProfileHeaderComponent)
